import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Shield, ArrowLeft, Loader2, AlertTriangle, RefreshCw, Filter, CheckCircle,
  Building2, Clock, PlayCircle, FileText
} from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const STATUS_LABEL = {
  RECEBIDA: 'Recebida', EM_TRIAGEM: 'Em Triagem', EM_INVESTIGACAO: 'Em Investigação',
  CONCLUIDA: 'Concluída', ARQUIVADA: 'Arquivada',
};
const STATUS_COLOR = {
  RECEBIDA: 'bg-blue-500/20 text-blue-200 border-blue-400/30',
  EM_TRIAGEM: 'bg-amber-500/20 text-amber-200 border-amber-400/30',
  EM_INVESTIGACAO: 'bg-indigo-500/20 text-indigo-200 border-indigo-400/30',
  CONCLUIDA: 'bg-emerald-500/20 text-emerald-200 border-emerald-400/30',
  ARQUIVADA: 'bg-slate-500/20 text-slate-300 border-slate-400/30',
};

/**
 * Consolidated cross-tenant denúncias list with bulk actions for the CogniSeg
 * global user. Route: /cogniseg/denuncias
 */
const CognisegReportsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [filters, setFilters] = useState({ status: 'RECEBIDA', tenant_id: 'all', urgency: 'all' });
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (filters.status && filters.status !== 'all') params.set('status', filters.status);
      if (filters.tenant_id && filters.tenant_id !== 'all') params.set('tenant_id', filters.tenant_id);
      if (filters.urgency && filters.urgency !== 'all') params.set('urgency', filters.urgency);
      const [{ data: r }, { data: t }] = await Promise.all([
        api.get(`/cogniseg/reports?${params.toString()}`),
        tenants.length === 0 ? api.get('/cogniseg/tenants') : Promise.resolve({ data: { tenants } }),
      ]);
      setReports(r.reports || []);
      if (tenants.length === 0) setTenants(t.tenants || []);
      setSelected(new Set());
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao carregar denúncias');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const toggleAll = () => {
    if (selected.size === reports.length) setSelected(new Set());
    else setSelected(new Set(reports.map(r => r.id)));
  };

  const applyAction = async (action) => {
    if (selected.size === 0) return;
    setApplying(true);
    setError(''); setSuccess('');
    try {
      const { data } = await api.post('/cogniseg/reports/bulk-action', {
        report_ids: Array.from(selected),
        action,
      });
      setSuccess(`${data.modified_count} denúncia(s) marcada(s) como ${STATUS_LABEL[data.new_status]}`);
      setTimeout(() => setSuccess(''), 4000);
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao aplicar ação');
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-950 text-slate-100">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/cogniseg')} className="p-2 rounded-lg hover:bg-white/5" data-testid="back-to-dashboard-btn">
              <ArrowLeft className="w-5 h-5 text-slate-300" />
            </button>
            <div className="p-2 rounded-xl bg-indigo-600/20 border border-indigo-500/30">
              <Shield className="w-6 h-6 text-indigo-300" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-indigo-300/80">CogniSeg</div>
              <h1 className="text-xl font-semibold text-white">Denúncias — Ações em Massa</h1>
            </div>
          </div>
          <Button variant="ghost" onClick={load} disabled={loading} className="text-slate-300 hover:bg-white/5">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Atualizar
          </Button>
        </div>

        {error && (
          <Alert className="mb-4 bg-red-900/30 border-red-500/40 text-red-100">
            <AlertTriangle className="w-4 h-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {success && (
          <Alert className="mb-4 bg-emerald-900/30 border-emerald-500/40 text-emerald-100" data-testid="bulk-success">
            <CheckCircle className="w-4 h-4" />
            <AlertDescription>{success}</AlertDescription>
          </Alert>
        )}

        {/* Filters */}
        <Card className="bg-white/5 border-white/10 mb-4">
          <CardContent className="p-4 flex flex-wrap items-center gap-3">
            <Filter className="w-4 h-4 text-slate-400" />
            <div className="min-w-[160px]">
              <Select value={filters.status} onValueChange={(v) => setFilters(f => ({ ...f, status: v }))}>
                <SelectTrigger className="bg-white/5 border-white/10 text-slate-100" data-testid="filter-status">
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os estados</SelectItem>
                  {Object.entries(STATUS_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[200px]">
              <Select value={filters.tenant_id} onValueChange={(v) => setFilters(f => ({ ...f, tenant_id: v }))}>
                <SelectTrigger className="bg-white/5 border-white/10 text-slate-100" data-testid="filter-tenant">
                  <SelectValue placeholder="Cliente" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os clientes</SelectItem>
                  {tenants.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[160px]">
              <Select value={filters.urgency} onValueChange={(v) => setFilters(f => ({ ...f, urgency: v }))}>
                <SelectTrigger className="bg-white/5 border-white/10 text-slate-100" data-testid="filter-urgency">
                  <SelectValue placeholder="Urgência" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Qualquer</SelectItem>
                  <SelectItem value="CRITICO">Crítico</SelectItem>
                  <SelectItem value="ALTO">Alto</SelectItem>
                  <SelectItem value="MEDIO">Médio</SelectItem>
                  <SelectItem value="BAIXO">Baixo</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Bulk actions bar (sticky) */}
        {selected.size > 0 && (
          <div className="mb-3 flex items-center gap-3 bg-indigo-600/20 border border-indigo-500/40 rounded-lg px-4 py-3" data-testid="bulk-toolbar">
            <span className="text-sm text-indigo-100">{selected.size} selecionada(s)</span>
            <div className="ml-auto flex gap-2">
              <Button size="sm" onClick={() => applyAction('mark_in_triage')} disabled={applying}
                      className="bg-amber-500 hover:bg-amber-600 text-slate-900" data-testid="bulk-mark-triage">
                <Clock className="w-4 h-4 mr-1" />
                Marcar Em Triagem
              </Button>
              <Button size="sm" onClick={() => applyAction('mark_in_investigation')} disabled={applying}
                      className="bg-indigo-500 hover:bg-indigo-600 text-white" data-testid="bulk-mark-investigation">
                <PlayCircle className="w-4 h-4 mr-1" />
                Marcar Em Investigação
              </Button>
            </div>
          </div>
        )}

        {/* Reports table */}
        <Card className="bg-white/5 border-white/10">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 flex items-center gap-3 text-slate-300">
                <Loader2 className="w-5 h-5 animate-spin" /> A carregar…
              </div>
            ) : reports.length === 0 ? (
              <div className="p-10 text-center text-slate-400">
                <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                Nenhuma denúncia encontrada para os filtros selecionados.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-slate-400 border-b border-white/10">
                    <tr>
                      <th className="p-3 w-12 text-left">
                        <input
                          type="checkbox"
                          checked={selected.size === reports.length}
                          onChange={toggleAll}
                          data-testid="select-all-checkbox"
                        />
                      </th>
                      <th className="p-3 text-left">Código</th>
                      <th className="p-3 text-left">Cliente</th>
                      <th className="p-3 text-left">Categoria</th>
                      <th className="p-3 text-left">Estado</th>
                      <th className="p-3 text-left">Urgência</th>
                      <th className="p-3 text-left">Data</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reports.map(r => (
                      <tr key={r.id}
                          className={`border-b border-white/5 hover:bg-white/5 ${selected.has(r.id) ? 'bg-indigo-500/10' : ''}`}
                          data-testid={`report-row-${r.tracking_code}`}>
                        <td className="p-3">
                          <input
                            type="checkbox"
                            checked={selected.has(r.id)}
                            onChange={() => toggle(r.id)}
                            data-testid={`select-${r.tracking_code}`}
                          />
                        </td>
                        <td className="p-3 font-mono text-slate-200">{r.tracking_code}</td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded flex items-center justify-center text-white text-xs font-semibold"
                                 style={{ background: r.tenant_color || '#0F172A' }}>
                              {(r.tenant_name || '?').slice(0, 1).toUpperCase()}
                            </div>
                            <span className="text-slate-200">{r.tenant_name}</span>
                          </div>
                        </td>
                        <td className="p-3 text-slate-300">{r.category}</td>
                        <td className="p-3">
                          <Badge className={STATUS_COLOR[r.status] || 'bg-slate-500/20'}>{STATUS_LABEL[r.status] || r.status}</Badge>
                        </td>
                        <td className="p-3 text-slate-300">{r.urgency || '—'}</td>
                        <td className="p-3 text-slate-400">
                          {r.created_at ? new Date(r.created_at).toLocaleDateString('pt-PT') : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="mt-6 text-xs text-slate-500 text-center">
          Sessão global CogniSeg · <span className="text-slate-400">{user?.email}</span> · <Link to="/cogniseg" className="underline hover:text-slate-300">Voltar ao dashboard</Link>
        </div>
      </div>
    </div>
  );
};

export default CognisegReportsPage;
